import React, { useEffect, useRef, useState } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import './quill-renderer.scss' // your custom SCSS
import { modules } from './quillModules' // import your config
import { Switch, Typography, Flex } from 'antd'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { setupQuillForTesting } from '../../../utils/e2e-test.helpers'

const { Text } = Typography

// Check if HTML contains formatting (ignore basic p and br tags)
const hasFormatting = html => {
    if (!html) return false

    // If there's no HTML content or it's just whitespace/empty, return false
    if (!html.trim()) return false

    // Extract all HTML tags
    const tagRegex = /<\/?[^>]+>/g
    const matches = html.match(tagRegex) || []

    // If there are no tags, it's plain text
    if (matches.length === 0) return false

    // Check each tag - if it's not a basic p or br tag, it's formatted
    for (const match of matches) {
        const lowerMatch = match.toLowerCase()

        // Skip checking the basic tags
        if (
            lowerMatch === '<p>' ||
            lowerMatch === '</p>' ||
            lowerMatch === '<br>' ||
            lowerMatch === '<br/>' ||
            lowerMatch === '</br>'
        ) {
            continue
        }

        // If tag has attributes (like <p style="...">), it's formatted
        if (lowerMatch.startsWith('<p ')) {
            return true
        }

        // Any other tag (not p or br) means it's formatted
        return true
    }

    // If we only found basic p and br tags, it's not formatted
    return false
}

// Helper function to clean Quill's empty state HTML
const cleanQuillHtml = html => {
    if (!html) {
        return ''
    }
    // Quill 2.x serializes spaces as &nbsp; under pre-wrap — normalize them back
    const normalized = html.replace(/(&nbsp;|&#160;|&#xA0;)/gi, ' ')
    // Remove common empty tags and whitespace, check if result is empty
    const cleaned = normalized.replace(/<p><br><\/p>|<p><\/p>|<br\s*\/?>/gi, '').trim()
    return cleaned === '' ? '' : normalized
}

const TextEditor = ({
    value,
    onChange,
    readOnly,
    placeholder,
    label,
    height,
    highlightRanges = [],
    advancedMode = false,
    testId, // Unique identifier for E2E testing (e.g., "hit-feedback", "miss-feedback")
}) => {
    const ref = useRef(null)
    const containerRef = useRef(null)
    const themeColors = useThemeColors()
    const isInternalChange = useRef(false)

    // Default to basic mode, but use advanced if content has formatting
    const [isAdvancedMode, setIsAdvancedMode] = useState(() => {
        if (advancedMode === undefined) {
            return hasFormatting(value)
        }
        return advancedMode
    })

    // Store HTML content
    const [htmlText, setHtmlText] = useState(value || '')

    // Update internal state only when value changes from outside (not from the editor itself)
    useEffect(() => {
        if (isInternalChange.current) {
            isInternalChange.current = false
            return
        }
        setHtmlText(value || '')
    }, [value])

    // Setup E2E testing when editor is ready
    useEffect(() => {
        if (ref.current) {
            setupQuillForTesting(ref, containerRef, testId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Apply highlights when highlightRanges changes
    useEffect(() => {
        if (!ref.current || !highlightRanges.length) return

        const quill = ref.current.getEditor()
        if (!quill) return

        // Clear existing highlights first by removing all background formatting
        const fullLength = quill.getLength()
        if (fullLength > 0) {
            quill.formatText(0, fullLength, { background: false }, 'silent')
        }

        // Apply new highlights
        highlightRanges.forEach(({ start, end, color, textColor, opacity = 1 }) => {
            const effectiveColor = color || themeColors.warning
            if (start >= 0 && end > start && end <= fullLength) {
                const formatOptions = {}

                // Apply background color with opacity
                if (effectiveColor) {
                    if (opacity < 1) {
                        // Convert hex to rgba if opacity is specified
                        const hexToRgba = (hex, alpha) => {
                            const r = parseInt(hex.slice(1, 3), 16)
                            const g = parseInt(hex.slice(3, 5), 16)
                            const b = parseInt(hex.slice(5, 7), 16)
                            return `rgba(${r}, ${g}, ${b}, ${alpha})`
                        }
                        formatOptions.background = effectiveColor.startsWith('#') ? hexToRgba(effectiveColor, opacity) : effectiveColor
                    } else {
                        formatOptions.background = effectiveColor
                    }
                }

                // Apply text color if specified
                if (textColor) {
                    formatOptions.color = textColor
                }

                quill.formatText(start, end - start, formatOptions, 'silent')
            }
        })
    }, [highlightRanges, themeColors.warning])

    // Handle toggle change
    const handleModeChange = checked => setIsAdvancedMode(checked)

    // Handle changes in editor
    const handleEditorChange = content => {
        isInternalChange.current = true
        setHtmlText(content)
        const cleanedContent = cleanQuillHtml(content)
        if (onChange) onChange(cleanedContent)
    }

    useEffect(() => {
        const handleKeyDown = e => {
            // Example: Ctrl+M to blur
            if (e.ctrlKey && e.key === 'm') {
                ref.current?.blur()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    // Configure modules based on mode
    const basicModules = {
        toolbar: false,
        clipboard: { matchVisual: false },
    }

    return (
        <div 
            ref={containerRef} 
            data-test={testId ? `text-editor-${testId}` : "text-editor-container"} 
            style={{ height }}
        >
            <Flex justify='space-between' align='center' style={{ marginBottom: '8px' }}>
                {label && <Text>{label}</Text>}

                {!readOnly && (
                    <label>
                        <Switch checked={isAdvancedMode} onChange={handleModeChange} className='mr-4' size='small' />
                        <Text>Advanced</Text>
                    </label>
                )}
            </Flex>

            <ReactQuill
                style={height ? { height: 'calc(100% - 50px)', display: 'flex', flexDirection: 'column' } : undefined}
                ref={ref}
                theme='snow'
                value={htmlText}
                onChange={handleEditorChange}
                modules={isAdvancedMode ? modules : basicModules}
                placeholder={placeholder}
                readOnly={readOnly}
            />
        </div>
    )
}

export default TextEditor
